(() => {
  const fieldLabels = {
    colors: 'Color',
    unlockMethod: 'Unlock Method',
    connectivity: 'Connectivity',
    lockStyle: 'Lock Style',
    smartFeatures: 'Smart Features',
    hardwareType: 'Hardware Type',
    material: 'Material',
    packQuantity: 'Pack Quantity',
    centerToCenter: 'Center-to-Center Size',
  };

  const fieldOrder = {
    'smart-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'keypad-locks': ['colors', 'price', 'unlockMethod', 'lockStyle', 'smartFeatures'],
    'door-lock-deadbolt': ['colors', 'price', 'unlockMethod', 'lockStyle', 'smartFeatures'],
    'door-hardware': ['colors', 'price', 'lockStyle', 'smartFeatures'],
    'cabinet-hardware': ['colors', 'price', 'hardwareType', 'material', 'packQuantity', 'centerToCenter'],
    'wi-fi-smart-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'bluetooth-smart-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'fingerprint-smart-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'wi-fi-smart-locks-with-handles': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'bluetooth-smart-locks-with-handles': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    accessories: ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'keypad-deadbolt-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'fingerprint-keypad-locks': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'keypad-deadbolt-locks-with-handles': ['colors', 'price', 'unlockMethod', 'connectivity', 'lockStyle', 'smartFeatures'],
    'deadbolt-locks': ['colors', 'price', 'unlockMethod', 'lockStyle', 'smartFeatures'],
    'door-knobs': ['colors', 'price', 'unlockMethod', 'lockStyle', 'smartFeatures'],
    'door-levers': ['colors', 'price', 'unlockMethod', 'lockStyle', 'smartFeatures'],
    'cabinet-pulls': ['colors', 'price', 'hardwareType', 'material', 'packQuantity', 'centerToCenter'],
    'cabinet-knobs': ['colors', 'price', 'hardwareType', 'material', 'packQuantity', 'centerToCenter'],
  };

  const swatches = {
    'matte black': '#111111',
    black: '#111111',
    'satin nickel': '#c9cbca',
    nickel: '#c9cbca',
    silver: '#c9cbca',
    'oil rubbed bronze': '#6b4226',
    bronze: '#8a5b32',
    'champagne bronze': '#b79062',
    gold: '#c9a45e',
    'brushed gold': '#c9a45e',
    white: '#f5f5f2',
  };

  const parseList = (value) => {
    if (!value || value === 'null') return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (_) {
      return [];
    }
  };

  const canonicalColor = (value) => {
    const normalized = String(value)
      .replace(/[\u2010-\u2015\u2212_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const labels = {
      'matte black': 'Matte Black',
      'satin nickel': 'Satin Nickel',
      'oil rubbed bronze': 'Oil Rubbed Bronze',
      'champagne bronze': 'Champagne Bronze',
      'brushed gold': 'Brushed Gold',
      black: 'Black',
      white: 'White',
      silver: 'Silver',
      bronze: 'Bronze',
      gold: 'Gold',
      nickel: 'Nickel',
    };
    return labels[normalized] || String(value).trim();
  };

  const formatCount = (count) => `${count} product${count === 1 ? '' : 's'}`;

  class VeiseCollectionFilters {
    constructor(scope) {
      this.scope = scope;
      this.root = scope.querySelector('[data-veise-filters]');
      this.panel = scope.querySelector('[data-veise-filter-panel]');
      this.products = [...scope.querySelectorAll('[data-veise-product]')].map((element) => ({
        element,
        price: Number(element.dataset.price || 0),
        colors: [...new Set(parseList(element.dataset.colors).map(canonicalColor))],
        unlockMethod: parseList(element.dataset.unlockMethod),
        connectivity: parseList(element.dataset.connectivity),
        lockStyle: parseList(element.dataset.lockStyle),
        smartFeatures: parseList(element.dataset.smartFeatures),
        hardwareType: parseList(element.dataset.hardwareType),
        material: parseList(element.dataset.material),
        packQuantity: parseList(element.dataset.packQuantity),
        centerToCenter: parseList(element.dataset.centerToCenter),
      }));
      this.handle = this.root.dataset.collectionHandle;
      this.fields = fieldOrder[this.handle] || ['colors', 'price'];
      this.selected = new Map(this.fields.filter((field) => field !== 'price').map((field) => [field, new Set()]));
      this.priceFloor = Math.floor(Math.min(...this.products.map((product) => product.price)) / 100);
      this.priceCeiling = Math.ceil(Math.max(...this.products.map((product) => product.price)) / 100);
      this.priceMin = this.priceFloor;
      this.priceMax = this.priceCeiling;
      this.loadState();
      this.build();
      this.bind();
      this.apply(false);
    }

    valuesFor(field) {
      const values = new Map();
      for (const product of this.products) {
        for (const value of product[field] || []) values.set(value, (values.get(value) || 0) + 1);
      }
      return [...values.entries()].sort((a, b) => {
        if (field === 'packQuantity') return Number(a[0]) - Number(b[0]);
        if (field === 'centerToCenter') return parseFloat(a[0]) - parseFloat(b[0]);
        return a[0].localeCompare(b[0]);
      });
    }

    loadState() {
      const params = new URLSearchParams(window.location.search);
      for (const [field, selected] of this.selected) {
        for (const value of params.getAll(`vf_${field}`)) selected.add(value);
      }
      if (params.has('vf_min')) {
        const min = Number(params.get('vf_min'));
        if (Number.isFinite(min)) this.priceMin = Math.max(this.priceFloor, Math.min(min, this.priceCeiling));
      }
      if (params.has('vf_max')) {
        const max = Number(params.get('vf_max'));
        if (Number.isFinite(max)) this.priceMax = Math.min(this.priceCeiling, Math.max(max, this.priceFloor));
      }
      if (this.priceMin > this.priceMax) [this.priceMin, this.priceMax] = [this.priceMax, this.priceMin];
    }

    build() {
      const groups = this.root.querySelector('[data-veise-filter-groups]');
      for (const field of this.fields) {
        if (field === 'price') {
          groups.append(this.buildPrice(true));
          continue;
        }
        const values = this.valuesFor(field);
        if (!values.length) continue;
        groups.append(this.buildList(field, values, true));
      }
    }

    groupShell(title, open) {
      const details = document.createElement('details');
      details.className = 'veise-filter-group';
      details.open = open;
      const summary = document.createElement('summary');
      summary.className = 'veise-filter-group__summary';
      const label = document.createElement('span');
      label.textContent = title;
      const chevron = document.createElement('span');
      chevron.className = 'veise-filter-group__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      summary.append(label, chevron);
      const content = document.createElement('div');
      content.className = 'veise-filter-group__content';
      details.append(summary, content);
      return { details, content };
    }

    buildList(field, values, open) {
      const { details, content } = this.groupShell(fieldLabels[field], open);
      for (const [value, count] of values) {
        const label = document.createElement('label');
        label.className = 'veise-filter-option';
        label.dataset.field = field;
        label.dataset.value = value;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = value;
        input.checked = this.selected.get(field).has(value);
        input.setAttribute('aria-label', `${fieldLabels[field]}: ${value}`);
        if (field === 'colors') {
          const swatch = document.createElement('span');
          swatch.className = 'veise-filter-swatch';
          swatch.style.setProperty('--veise-swatch', swatches[value.toLowerCase()] || '#d8dde0');
          label.append(swatch);
        }
        const text = document.createElement('span');
        text.className = 'veise-filter-option__label';
        text.textContent = field === 'packQuantity' ? `${value} Pack` : value;
        const number = document.createElement('span');
        number.className = 'veise-filter-option__count';
        number.dataset.optionCount = '';
        number.textContent = count;
        label.append(input, text, number);
        content.append(label);
      }
      return details;
    }

    buildPrice(open) {
      const { details, content } = this.groupShell('Price', open);
      const wrapper = document.createElement('div');
      wrapper.className = 'veise-filter-price';
      const fields = document.createElement('div');
      fields.className = 'veise-filter-price__fields';
      const createField = (kind, value) => {
        const label = document.createElement('label');
        label.className = 'veise-filter-price__field';
        const currency = document.createElement('span');
        currency.textContent = '$';
        const input = document.createElement('input');
        input.type = 'number';
        input.min = this.priceFloor;
        input.max = this.priceCeiling;
        input.value = value;
        input.dataset[`price${kind}`] = '';
        label.append(currency, input);
        return label;
      };
      const separator = document.createElement('span');
      separator.className = 'veise-filter-price__separator';
      separator.textContent = 'to';
      fields.append(createField('Min', this.priceMin), separator, createField('Max', this.priceMax));

      const ranges = document.createElement('div');
      ranges.className = 'veise-filter-price__ranges';
      ranges.dataset.priceRanges = '';
      for (const [kind, value] of [['Min', this.priceMin], ['Max', this.priceMax]]) {
        const input = document.createElement('input');
        input.type = 'range';
        input.min = this.priceFloor;
        input.max = this.priceCeiling;
        input.value = value;
        input.dataset[`range${kind}`] = '';
        ranges.append(input);
      }
      wrapper.append(fields, ranges);
      content.append(wrapper);
      return details;
    }

    bind() {
      this.root.addEventListener('change', (event) => {
        const option = event.target.closest('.veise-filter-option');
        if (option) {
          const selected = this.selected.get(option.dataset.field);
          event.target.checked ? selected.add(option.dataset.value) : selected.delete(option.dataset.value);
          this.apply();
        }
      });
      this.root.addEventListener('input', (event) => {
        if (event.target.matches('[data-price-min], [data-price-max], [data-range-min], [data-range-max]')) this.syncPrice(event.target);
      });
      this.root.querySelector('[data-veise-clear]').addEventListener('click', () => this.clear());
      this.scope.querySelector('[data-veise-clear-results]')?.addEventListener('click', () => this.clear());
      this.root.querySelector('[data-veise-active-filters]').addEventListener('click', (event) => {
        const chip = event.target.closest('[data-chip-field]');
        if (!chip) return;
        const field = chip.dataset.chipField;
        if (field === 'price') {
          this.priceMin = this.priceFloor;
          this.priceMax = this.priceCeiling;
          this.syncPriceControls();
        } else {
          this.selected.get(field).delete(chip.dataset.chipValue);
          const input = [...this.root.querySelectorAll('.veise-filter-option input')].find((item) => item.closest('.veise-filter-option').dataset.field === field && item.value === chip.dataset.chipValue);
          if (input) input.checked = false;
        }
        this.apply();
      });
      this.scope.querySelectorAll('[data-veise-filter-open]').forEach((button) => button.addEventListener('click', () => this.open()));
      this.scope.querySelectorAll('[data-veise-filter-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel.classList.contains('is-open')) this.close();
      });
    }

    syncPrice(target) {
      const isMin = target.hasAttribute('data-price-min') || target.hasAttribute('data-range-min');
      const number = Math.max(this.priceFloor, Math.min(Number(target.value || 0), this.priceCeiling));
      if (isMin) this.priceMin = Math.min(number, this.priceMax);
      else this.priceMax = Math.max(number, this.priceMin);
      this.syncPriceControls();
      this.apply();
    }

    syncPriceControls() {
      this.root.querySelector('[data-price-min]').value = this.priceMin;
      this.root.querySelector('[data-price-max]').value = this.priceMax;
      this.root.querySelector('[data-range-min]').value = this.priceMin;
      this.root.querySelector('[data-range-max]').value = this.priceMax;
      const span = Math.max(1, this.priceCeiling - this.priceFloor);
      const ranges = this.root.querySelector('[data-price-ranges]');
      ranges.style.setProperty('--veise-price-start', `${((this.priceMin - this.priceFloor) / span) * 100}%`);
      ranges.style.setProperty('--veise-price-end', `${((this.priceMax - this.priceFloor) / span) * 100}%`);
    }

    matches(product, omitField = '') {
      const dollars = product.price / 100;
      if (omitField !== 'price' && (dollars < this.priceMin || dollars > this.priceMax)) return false;
      for (const [field, selected] of this.selected) {
        if (field === omitField || !selected.size) continue;
        if (!(product[field] || []).some((value) => selected.has(value))) return false;
      }
      return true;
    }

    apply(updateUrl = true) {
      let visible = 0;
      for (const product of this.products) {
        const matches = this.matches(product);
        product.element.hidden = !matches;
        if (matches) visible += 1;
      }
      this.root.querySelector('[data-veise-result-count]').textContent = formatCount(visible);
      this.root.querySelector('[data-veise-mobile-result-count]').textContent = visible;
      const empty = this.scope.querySelector('[data-veise-filter-empty]');
      if (empty) empty.hidden = visible !== 0;
      this.updateOptionCounts();
      this.updateActive();
      this.syncPriceControls();
      if (updateUrl) this.updateUrl();
    }

    updateOptionCounts() {
      for (const option of this.root.querySelectorAll('.veise-filter-option')) {
        const field = option.dataset.field;
        const value = option.dataset.value;
        const count = this.products.filter((product) => this.matches(product, field) && (product[field] || []).includes(value)).length;
        option.querySelector('[data-option-count]').textContent = count;
        const input = option.querySelector('input');
        input.disabled = count === 0 && !input.checked;
        option.classList.toggle('is-disabled', input.disabled);
      }
    }

    updateActive() {
      const active = this.root.querySelector('[data-veise-active-filters]');
      active.replaceChildren();
      let count = 0;
      const addChip = (field, value, text) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'veise-filter-chip';
        button.dataset.chipField = field;
        if (value) button.dataset.chipValue = value;
        const label = document.createElement('span');
        label.textContent = text;
        button.append(label);
        button.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="m2 2 8 8M10 2 2 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>');
        active.append(button);
        count += 1;
      };
      for (const [field, selected] of this.selected) {
        for (const value of selected) addChip(field, value, value);
      }
      if (this.priceMin !== this.priceFloor || this.priceMax !== this.priceCeiling) addChip('price', '', `$${this.priceMin}–$${this.priceMax}`);
      active.hidden = count === 0;
      this.root.querySelector('[data-veise-clear]').hidden = count === 0;
      this.scope.querySelectorAll('[data-veise-active-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
    }

    updateUrl() {
      const url = new URL(window.location.href);
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('vf_')) url.searchParams.delete(key);
      for (const [field, selected] of this.selected) for (const value of selected) url.searchParams.append(`vf_${field}`, value);
      if (this.priceMin !== this.priceFloor) url.searchParams.set('vf_min', this.priceMin);
      if (this.priceMax !== this.priceCeiling) url.searchParams.set('vf_max', this.priceMax);
      window.history.replaceState({}, '', url);
    }

    clear() {
      for (const selected of this.selected.values()) selected.clear();
      this.root.querySelectorAll('.veise-filter-option input').forEach((input) => { input.checked = false; });
      this.priceMin = this.priceFloor;
      this.priceMax = this.priceCeiling;
      this.apply();
    }

    open() {
      this.panel.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      this.root.querySelector('[data-veise-filter-close]').focus();
    }

    close() {
      this.panel.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      this.scope.querySelector('[data-veise-filter-open]')?.focus();
    }
  }

  const init = () => document.querySelectorAll('[data-veise-filter-scope]').forEach((scope) => {
    const root = scope.querySelector('[data-veise-filters]');
    if (root && !root.dataset.veiseFiltersReady) {
      root.dataset.veiseFiltersReady = 'true';
      new VeiseCollectionFilters(scope);
    }
  });

  let initFrame = 0;
  const scheduleInit = () => {
    if (initFrame) return;
    initFrame = window.requestAnimationFrame(() => {
      initFrame = 0;
      init();
    });
  };

  const observeSectionRefreshes = () => {
    init();
    new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.('[data-veise-filter-scope]') || node.querySelector?.('[data-veise-filter-scope]'))
      ))) scheduleInit();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeSectionRefreshes, { once: true });
  else observeSectionRefreshes();
  document.addEventListener('shopify:section:load', scheduleInit);
})();
